import { api } from './api';
import type { DeleteResponse, PaginatedResponse } from '../types';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface DataSourceMongoConfig {
  /** True when a URI is stored (aes:// ref). The raw URI is never returned from the server. */
  hasUri?: boolean;
  rawUri?: string | null;  // write-only – send to save a new URI, never received back
  dbName?: string | null;
  minPoolSize?: number;
  maxPoolSize?: number;
  maxIdleTimeMS?: number;
  serverSelectionTimeoutMS?: number;
  socketTimeoutMS?: number;
}

export interface DataSourceApiConfig {
  baseUrl?: string | null;
  credentialRef?: string | null;
  tokenEndpoint?: string | null;
  timeoutMs?: number;
}

export interface DataSourceAgentConfig {
  rendezvousId?: string | null;
  publicKeyRef?: string | null;
}

export interface DataSourceConfig {
  connector: 'internal' | 'mongo' | 'api' | 'agent';
  direction: 'inbound' | 'outbound';
  custody: 'platform' | 'external_vault' | 'ephemeral' | 'none';
  authStrategy: 'direct_db' | 'passthrough' | 'service_header';
  constantsSource: 'seeded' | 'control_db';
  mongo?: DataSourceMongoConfig | null;
  api?: DataSourceApiConfig | null;
  agent?: DataSourceAgentConfig | null;
  status: 'UNCONFIGURED' | 'ACTIVE' | 'ERROR';
  lifecycle: 'created' | 'configured' | 'tested' | 'active' | 'suspended';
  lastTestedAt?: string | null;
  lastError?: string | null;
}

export interface AdminAccessConfig {
  breakGlass: 'disabled' | 'active' | 'requested';
  activeReason?: string | null;
  requestedBy?: string | null;
  expiresAt?: string | null;
}

export interface ProjectSettingsAudit {
  lastModifiedBy?: string | null;
  lastModifiedAt?: string | null;
  lastModifiedIp?: string | null;
}

// --- Authentication methods (which login methods a project allows) ---
export interface AuthMethodToggle {
  enabled: boolean;
}

export interface OAuthProviderConfig {
  enabled: boolean;
  clientId?: string | null;
  clientSecretRef?: string | null;   // read: never returned; write: use rawClientSecret
  rawClientSecret?: string | null;   // write-only; sealed to aes:// on save
  hasClientSecret?: boolean;         // read-only flag (a secret is stored)
  allowedDomains?: string[];
}

export interface MicrosoftProviderConfig extends OAuthProviderConfig {
  tenantId: string;                  // 'common' (multi-tenant) or a directory GUID
}

export interface AuthMethodsConfig {
  mpin: AuthMethodToggle;
  password: AuthMethodToggle;
  google: OAuthProviderConfig;
  microsoft: MicrosoftProviderConfig;
}

export interface ProjectAuthSettings {
  schemaVersion: number;
  methods: AuthMethodsConfig;
}

// --- RBAC policy: whether the project uses an org-unit hierarchy ---
export type UnitModel = 'required' | 'none';

export interface ProjectRbacSettings {
  /** 'required' (default) = unit mandatory on personnel & role bindings; 'none' = unit-less project. */
  unitModel: UnitModel;
}

export interface ProjectSettings {
  schemaVersion: number;
  profileGeneration: number;
  // Neither `integrations` nor `backgroundJobs`. Both are Application
  // Configuration now — `Integration.<Channel>.<Provider>.*` and
  // `BackgroundJobs.*` — read and written on their own screen. This endpoint
  // neither returns them nor accepts them; a write carrying either is refused
  // with a 400 naming where they went, rather than silently dropped.
  //
  // What is left here is what genuinely belongs to the PROJECT: where its data
  // lives, and who may break glass into it.
  dataSource: DataSourceConfig;
  adminAccess?: AdminAccessConfig | null;
  auth?: ProjectAuthSettings | null;
  rbac?: ProjectRbacSettings | null;
  audit?: ProjectSettingsAudit | null;
}

export interface DataSourceTestRequest {
  connector?: 'internal' | 'mongo' | 'api' | 'agent';
  rawUri?: string;
  uriRef?: string;
  dbName?: string;
  serverSelectionTimeoutMS?: number;
}

export interface DataSourceTestResponse {
  success: boolean;
  status: 'ACTIVE' | 'ERROR';
  latencyMs?: number | null;
  message: string;
  testedAt: string;
  error?: string | null;
}

// Types
export interface Project {
  _id: string;
  name: string;
  appCode: string;
  description?: string;
  /** Optional logo/cover image URL shown on the login/project selector. */
  imageUrl?: string | null;
  partnerMasterId?: string | null;
  /** Project start date (ISO string). */
  startAt?: string | null;
  /** Whether the project's payment is done (not applicable for internal partners). */
  paymentDone?: boolean;
  projectSettings?: ProjectSettings | null;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  isDelete?: boolean;
  isDeleted?: boolean;
}

export interface ProjectCreateRequest {
  name: string;
  appCode: string;
  description?: string;
  imageUrl?: string | null;
  partnerMasterId?: string | null;
  startAt?: string | null;
  paymentDone?: boolean;
}

export interface ProjectUpdateRequest {
  name?: string;
  appCode?: string;
  description?: string;
  imageUrl?: string | null;
  partnerMasterId?: string | null;
  startAt?: string | null;
  paymentDone?: boolean;
}

export interface ProjectQueryParams {
  include_deleted?: boolean;
  is_active?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /** Per-column filters as a JSON string: {"field":{"value":..,"matchMode":".."}} */
  filters?: string;
}

// NOTE: the collection is now `project_master`, but the backend API path is still
// `/api/v1/apps/*` and the entity's code field is still `appCode` — both left as-is.
/** A project the caller may use (for the project dropdown / scoping). */
export interface AccessibleProject {
  _id: string;
  name: string;
  appCode: string;
}

/** Public (pre-auth) project code for the login project selector. */
export interface PublicProjectCode {
  appCode: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
}

/** Header branding for the logged-in project (resolved from the token's appId).
 *  logoUrl is a short-lived SAS URL (or external URL); null when no logo is set. */
export interface ProjectBranding {
  appCode: string | null;
  name: string | null;
  logoUrl: string | null;
}

export const projectsService = {
  // Public, UNAUTHENTICATED list of active project codes — used by the login screen
  // so a user can pick which project to sign in to. Returns {appCode, name,
  // description, imageUrl} for a rich selector.
  getPublicCodes: async (): Promise<PublicProjectCode[]> => {
    const response = await api.get<{ data: PublicProjectCode[] }>('/api/v1/apps/public-codes');
    return response.data.data || [];
  },

  // Header branding (logo + name) for the caller's own project — from the token's appId.
  getCurrentBranding: async (): Promise<ProjectBranding> => {
    const response = await api.get<ApiResponse<ProjectBranding>>('/api/v1/apps/current/branding');
    return response.data.data;
  },

  // Get all projects
  getAll: async (params?: ProjectQueryParams): Promise<PaginatedResponse<Project>> => {
    const response = await api.get<PaginatedResponse<Project>>('/api/v1/apps/list', { params });
    return response.data;
  },

  // Projects the caller may access (platform admin = all, customer admin = partner's, else own).
  getAccessible: async (): Promise<AccessibleProject[]> => {
    const response = await api.get<{ data: AccessibleProject[] }>('/api/v1/apps/accessible');
    return response.data.data || [];
  },

  // Get all projects for dropdown (no pagination, fetches all records)
  getAllForDropdown: async (): Promise<Project[]> => {
    const response = await api.get<PaginatedResponse<Project>>('/api/v1/apps/list', {
      params: { include_deleted: false },
    });
    return response.data.data || [];
  },

  // Get project by ID
  getById: async (id: string): Promise<Project> => {
    const response = await api.get<ApiResponse<Project>>(`/api/v1/apps/get/${id}`);
    return response.data.data;
  },

  // Create new project
  create: async (payload: ProjectCreateRequest): Promise<Project> => {
    const response = await api.post<ApiResponse<Project>>('/api/v1/apps/create', payload);
    return response.data.data;
  },

  // Update project
  update: async (id: string, payload: ProjectUpdateRequest): Promise<Project> => {
    const response = await api.put<ApiResponse<Project>>(`/api/v1/apps/update/${id}`, payload);
    return response.data.data;
  },

  // Delete project (soft delete)
  delete: async (id: string): Promise<DeleteResponse> => {
    const response = await api.delete<ApiResponse<DeleteResponse>>(`/api/v1/apps/delete/${id}`);
    return response.data.data;
  },

  // Restore deleted project
  restore: async (id: string): Promise<Project> => {
    const response = await api.patch<ApiResponse<Project>>(`/api/v1/apps/restore/${id}`);
    return response.data.data;
  },

  // Project Settings (Stage 4)
  getSettings: async (id: string): Promise<ProjectSettings> => {
    const response = await api.get<ApiResponse<ProjectSettings>>(`/api/v1/apps/get/${id}/settings`);
    return response.data.data;
  },

  updateSettings: async (id: string, payload: Partial<ProjectSettings>): Promise<ProjectSettings> => {
    const response = await api.put<ApiResponse<ProjectSettings>>(`/api/v1/apps/update/${id}/settings`, payload);
    return response.data.data;
  },

  testDataSource: async (id: string, payload: DataSourceTestRequest): Promise<DataSourceTestResponse> => {
    const response = await api.post<ApiResponse<DataSourceTestResponse>>(`/api/v1/apps/${id}/datasource/test`, payload);
    return response.data.data;
  },
};
